import { Canceler } from 'axios';
import cloneDeep from 'lodash.clonedeep';
import { action, computed, makeObservable, observable } from 'mobx';
import {
  CUSTOMER_WALLET_MODAL_MODE,
  defaultCustomerWalletModalCorrection,
  defaultCustomerWalletModalTopUp,
  defaultCustomerWalletModalValidation,
} from '../constants';
import { ICustomerWalletModalCorrection, ICustomerWalletModalTopUp, ICustomerWalletModalValidation } from '../types';
import { VALIDITY_PERIOD } from 'src/constants/main';
import { modalStore } from 'src/mobx/modalStore';
import client from 'src/services/client';
import { IValidation } from 'src/types/main';
import { getCancelToken } from 'src/utils/helpers';
import { emptyNumberValidation, emptyStringValidation } from 'src/utils/validation';
import { localeStore } from '~/mobx/localesStore';
import logger from '~/services/logger';
import { toastSuccess } from '~/utils/toasts';

const controlsHeight: number = 330; // modal header + selectMode block + modal footer + scrollable block margin
const topUpBlockHeight: number = 283;
const correctionBlockHeight: number = 180;
const errorHeight: number = 27;

class CustomerWalletModalStore {
  constructor() {
    makeObservable(this);
  }

  cancel: Canceler | null = null;

  @observable loading: boolean = false;

  @observable mode: CUSTOMER_WALLET_MODAL_MODE = CUSTOMER_WALLET_MODAL_MODE.TOP_UP;

  @observable correction: ICustomerWalletModalCorrection = { ...defaultCustomerWalletModalCorrection };
  @observable topUp: ICustomerWalletModalTopUp = { ...defaultCustomerWalletModalTopUp };

  @computed get customerId(): number | null {
    return modalStore.customerWalletModal?.customerId || null;
  }

  @computed get currentWalletNumbers(): number | null {
    return modalStore.customerWalletModal?.currentWalletsNumber || null;
  }

  @computed get isTopUpMode(): boolean {
    return this.mode === CUSTOMER_WALLET_MODAL_MODE.TOP_UP;
  }

  @computed get modalHeight(): number | undefined {
    if (!modalStore.customerWalletModal) return undefined;

    const height: number = controlsHeight + (this.isTopUpMode ? topUpBlockHeight : correctionBlockHeight);
    let errorsNumber: number = 0;

    if (!this.validation.isValid) {
      Object.keys(this.validation).forEach((key, index) => {
        if (!(this.validation[key as keyof ICustomerWalletModalValidation] as IValidation).isValid && index) {
          errorsNumber++;
        }
      });
    }

    return height + errorsNumber * errorHeight;
  }

  public initStore = () => {
    this.loading = false;

    this.mode = CUSTOMER_WALLET_MODAL_MODE.TOP_UP;

    this.correction = { ...defaultCustomerWalletModalCorrection };
    this.topUp = { ...defaultCustomerWalletModalTopUp };

    this.validation = { ...defaultCustomerWalletModalValidation };
  };

  @action changeMode = (mode: CUSTOMER_WALLET_MODAL_MODE) => {
    this.mode = mode;
    // update validation
    if (!this.validation.isValid) this.setValidation();
  };

  @action updateAmount = (amount: string | undefined) => {
    if (this.isTopUpMode) {
      this.topUp.amount = amount;
    } else {
      this.correction.amount = this.currentWalletNumbers && Number(amount) > this.currentWalletNumbers ? String(this.currentWalletNumbers) : amount;
    }

    // update validation
    if (!this.validation.amount.isValid) this.setValidation();
  };

  @action updateReason = (reason: string | undefined) => {
    if (this.isTopUpMode) {
      this.topUp.reason = reason;
    } else {
      this.correction.reason = reason;
    }

    // update validation
    if (!this.validation.reason.isValid) this.setValidation();
  };

  @action updatePeriodState = (duration: VALIDITY_PERIOD) => {
    this.topUp.periodState = duration;
    // update validation
    if (!this.validation.period.isValid) this.setValidation();
  };
  @action updatePeriodValue = (value: string | undefined) => {
    this.topUp.periodValue = value ? Number(value) : undefined;
    // update validation
    if (!this.validation.period.isValid) this.setValidation();
  };

  public submit = () => {
    if (this.isTopUpMode) {
      this.submitTopUp();
    } else {
      this.submitCorrect();
    }
  };

  private setSuccess = () => {
    if (modalStore.customerWalletModal?.onSave) {
      const amount: number = Number(this.isTopUpMode ? this.topUp.amount : this.correction.amount) || 0;
      modalStore.customerWalletModal?.onSave(amount, this.mode);
    }

    toastSuccess({ message: localeStore.t('wallet_update_success') });
    this.closeModal();
  };

  @action submitTopUp = async () => {
    try {
      if (!this.customerId) return;
      if (this.cancel) this.cancel('Cancelled');
      this.loading = true;

      const params = {
        type: 1,
        user_id: this.customerId,
        amount: Number(this.topUp.amount),
        validity: this.topUp.periodState,
        validity_amount: this.topUp.periodState === VALIDITY_PERIOD.FOREVER ? undefined : Number(this.topUp.periodValue),
        reason: this.topUp.reason,
      };

      await client.post(`/v3/wallets`, params, { cancelToken: getCancelToken((c) => (this.cancel = c)) });

      this.loading = false;
      this.setSuccess();
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'CustomerWalletModalStore submitTopUp', showModal: true });
    }
  };

  @action submitCorrect = async () => {
    try {
      if (!this.customerId) return;
      if (this.cancel) this.cancel('Cancelled');
      this.loading = true;

      const params = {
        user_id: this.customerId,
        correct_amount: Number(this.correction.amount),
        correct_reason: this.correction.reason,
      };

      await client.post(`/v3/wallets/correct`, params, { cancelToken: getCancelToken((c) => (this.cancel = c)) });

      this.loading = false;
      this.setSuccess();
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'CustomerWalletModalStore submitCorrect', showModal: true });
    }
  };

  @observable validation: ICustomerWalletModalValidation = cloneDeep(defaultCustomerWalletModalValidation);
  @action setValidation = () => {
    this.validation = cloneDeep(defaultCustomerWalletModalValidation);

    if (this.isTopUpMode) {
      // amount validation
      [this.validation.amount.isValid, this.validation.isValid, this.validation.amount.errorText] = emptyNumberValidation(
        this.topUp.amount,
        this.validation.amount.isValid,
        this.validation.isValid,
        true
      );

      // reason validation
      [this.validation.reason.isValid, this.validation.isValid, this.validation.reason.errorText] = emptyStringValidation(
        this.topUp.reason,
        this.validation.reason.isValid,
        this.validation.isValid
      );

      // period validation
      if (this.topUp.periodState !== VALIDITY_PERIOD.FOREVER) {
        [this.validation.period.isValid, this.validation.isValid, this.validation.period.errorText] = emptyNumberValidation(
          this.topUp.periodValue,
          this.validation.period.isValid,
          this.validation.isValid,
          true
        );
      }
    } else {
      // if there are no "wallets", then all fields are disabled and validation is not needed
      if (!this.currentWalletNumbers) return;

      // amount validation
      [this.validation.amount.isValid, this.validation.isValid, this.validation.amount.errorText] = emptyNumberValidation(
        this.correction.amount,
        this.validation.amount.isValid,
        this.validation.isValid,
        true
      );

      // reason validation
      [this.validation.reason.isValid, this.validation.isValid, this.validation.reason.errorText] = emptyStringValidation(
        this.correction.reason,
        this.validation.reason.isValid,
        this.validation.isValid
      );
    }
  };

  closeModal = () => {
    modalStore.closeCustomerWalletModal();
    this.clearStore();
  };
  private clearStore = () => {
    if (this.cancel) this.cancel('Cancelled');
  };
}

export const customerWalletModalStore = new CustomerWalletModalStore();
